import { useState, useRef, useCallback } from 'react';
import { apiFetch } from '~/lib/api';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

interface UploadResult {
  _id: string;
  skills: string[];
  parsed_length: number;
  original_url: string;
}

export default function JobApplyCVPage() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return 'Solo se aceptan archivos PDF o DOCX.';
    }
    if (f.size > MAX_SIZE) {
      return 'El archivo no puede superar 5 MB.';
    }
    return null;
  }, []);

  const handleFileSelect = useCallback(
    (f: File) => {
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(f);
      setError(null);
      setResult(null);
    },
    [validateFile],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFileSelect(dropped);
    },
    [handleFileSelect],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFileSelect(selected);
    },
    [handleFileSelect],
  );

  const upload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/job-apply/api/cv/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type — browser sets multipart boundary automatically
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Error ${res.status}`);
      }
      const data: UploadResult = await res.json();
      setResult(data);
      setFile(null);
    } catch (err: any) {
      setError(err.message || 'Error al subir el archivo. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }, [file]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Tu hoja de vida</h1>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Sube tu hoja de vida</h2>
          <p className="text-base-content/70">
            Sube un archivo PDF o DOCX. Extraeremos tus habilidades y
            experiencia para personalizar las postulaciones.
          </p>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={onInputChange}
          />

          <div
            role="button"
            tabIndex={0}
            className={`border-2 border-dashed rounded-box p-12 text-center mt-4 transition-colors cursor-pointer ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-base-300 hover:border-primary'
            }`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="w-10 h-10 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                  />
                </svg>
                <p className="text-lg font-medium">{file.name}</p>
                <p className="text-sm text-base-content/50">
                  {(file.size / 1024).toFixed(0)} KB
                </p>
              </div>
            ) : (
              <div className="text-base-content/50">
                <svg
                  className="w-10 h-10 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                  />
                </svg>
                <p className="text-lg font-medium">
                  Arrastra tu hoja de vida aqui
                </p>
                <p className="text-sm mt-1">
                  o haz clic para buscar (PDF, DOCX — max 5 MB)
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-error text-sm mt-4">{error}</div>
          )}

          {file && !result && (
            <div className="card-actions justify-end mt-4">
              <button
                className="btn btn-primary"
                onClick={upload}
                disabled={uploading}
              >
                {uploading && (
                  <span className="loading loading-spinner loading-sm" />
                )}
                Subir hoja de vida
              </button>
            </div>
          )}

          {result && (
            <div className="mt-6 space-y-4">
              <div className="alert alert-success">
                Hoja de vida procesada correctamente
              </div>

              <div>
                <h3 className="font-semibold mb-2">Habilidades detectadas</h3>
                <div className="flex flex-wrap gap-2">
                  {result.skills.length > 0 ? (
                    result.skills.map((skill) => (
                      <span key={skill} className="badge badge-primary">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-base-content/50 text-sm">
                      No se detectaron habilidades.
                    </p>
                  )}
                </div>
              </div>

              <p className="text-sm text-base-content/50">
                Se extrajeron {result.parsed_length.toLocaleString()} caracteres
                del documento.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
